import {FC, createContext, useContext, useState, useEffect} from 'react'
import {DefaultConfig} from './_LayoutConfig'
import {
  setLayoutIntoLocalStorage,
  getEmptyCssClasses,
  getEmptyCSSVariables,
  getEmptyHTMLAttributes,
  LayoutSetup,
} from './_LayoutSetup'
import {
  ILayout,
  ILayoutCSSVariables,
  ILayoutCSSClasses,
  ILayoutHTMLAttributes,
  LayoutType,
  ToolbarType,
} from './_Models'
import {WithChildren} from '../../helpers'

export type OptionType = {
  value: string
  label: string
}
export interface LayoutContextModel {
  config: ILayout
  classes: ILayoutCSSClasses
  attributes: ILayoutHTMLAttributes
  cssVariables: ILayoutCSSVariables
  setLayout: (config: LayoutSetup) => void
  setLayoutType: (layoutType: LayoutType) => void
  setToolbarType: (toolbarType: ToolbarType) => void
  company: OptionType
  setCompany: React.Dispatch<React.SetStateAction<OptionType>>
  financialYear: OptionType
  fyStartDate: string
  setFyStartDate: React.Dispatch<React.SetStateAction<string>>
  fyEndDate: string
  setFyEndDate: React.Dispatch<React.SetStateAction<string>>
  setFinancialYear: React.Dispatch<React.SetStateAction<OptionType>>
  trigger: boolean
  setTrigger: React.Dispatch<React.SetStateAction<boolean>>
  companyOptions: OptionType[]
  setCompanyOptions: React.Dispatch<React.SetStateAction<OptionType[]>>
}

const LayoutContext = createContext<LayoutContextModel>({
  config: DefaultConfig,
  classes: getEmptyCssClasses(),
  attributes: getEmptyHTMLAttributes(),
  cssVariables: getEmptyCSSVariables(),
  setLayout: (config: LayoutSetup) => {},
  setLayoutType: (layoutType: LayoutType) => {},
  setToolbarType: (toolbarType: ToolbarType) => {},
  company: {label: '', value: ''},
  financialYear: {label: '', value: ''},
  fyStartDate: '',
  setFyStartDate: () => {},
  fyEndDate: '',
  setFyEndDate: () => {},
  setCompany: () => {},
  setFinancialYear: () => {},
  trigger: false,
  setTrigger: () => {},
  companyOptions: [],
  setCompanyOptions: () => {},
})

const enableSplashScreen = () => {
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'flex')
  }
}

const disableSplashScreen = () => {
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'none')
  }
}

const LayoutProvider: FC<WithChildren> = ({children}) => {
  const [config, setConfig] = useState(LayoutSetup.config)
  const [classes, setClasses] = useState(LayoutSetup.classes)
  const [attributes, setAttributes] = useState(LayoutSetup.attributes)
  const [cssVariables, setCSSVariables] = useState(LayoutSetup.cssVariables)
  const [companyOptions, setCompanyOptions] = useState<OptionType[]>([])
  const [trigger, setTrigger] = useState(true)

  const [company, setCompany] = useState<OptionType>(() => {
    const savedCompany = localStorage.getItem('company')
    if (savedCompany) {
      try {
        const parsed = JSON.parse(savedCompany)
        // console.log(parsed)
        if (parsed && typeof parsed.value === 'string' && typeof parsed.label === 'string') {
          return parsed
        }
      } catch (e) {
        console.error('Error parsing saved company:', e)
      }
    }
    return {label: '', value: ''}
  })

  const [financialYear, setFinancialYear] = useState<OptionType>({
    label: localStorage.getItem('financialYear') || '',
    value: localStorage.getItem('financialYear') || '',
  })

  const [fyStartDate, setFyStartDate] = useState<string>(localStorage.getItem('fyStartDate') || '')
  const [fyEndDate, setFyEndDate] = useState<string>(localStorage.getItem('fyEndDate') || '')
  // console.log(company)
  // console.log(financialYear)
  // console.log(fyStartDate)
  // console.log(fyEndDate)

  useEffect(() => {
    if (company && company.value) localStorage.setItem('company', JSON.stringify(company))
    else
      localStorage.setItem(
        'company',
        JSON.stringify({
          label: '',
          value: '',
        })
      )
  }, [company])

  useEffect(() => {
    if (financialYear && financialYear.value)
      localStorage.setItem('financialYear', financialYear?.value)
    else localStorage.setItem('financialYear', '')
  }, [financialYear])

  useEffect(() => {
    if (fyStartDate) localStorage.setItem('fyStartDate', fyStartDate)
    else localStorage.setItem('fyStartDate', '')
  }, [fyStartDate])

  useEffect(() => {
    if (fyEndDate) localStorage.setItem('fyEndDate', fyEndDate)
    else localStorage.setItem('fyEndDate', '')
  }, [fyEndDate])

  // useEffect(() => {
  //   if (!companyOptions.length) {
  //     localStorage.removeItem('fyEndDate')
  //     localStorage.removeItem('fyStartDate')
  //     localStorage.removeItem('financialYear')
  //     localStorage.removeItem('company')
  //     // setCompany({
  //     //   label: '',
  //     //   value: '',
  //     // })
  //     // setFinancialYear({label: '', value: ''})
  //     // setFyStartDate('')
  //     // setFyEndDate('')
  //   }
  // }, [companyOptions.length])

  const setLayout = (_themeConfig: Partial<ILayout>) => {
    enableSplashScreen()
    const bodyClasses = Array.from(document.body.classList)
    bodyClasses.forEach((cl) => document.body.classList.remove(cl))
    const updatedConfig = LayoutSetup.updatePartialConfig(_themeConfig)
    setConfig(Object.assign({}, updatedConfig))
    setClasses(LayoutSetup.classes)
    setAttributes(LayoutSetup.attributes)
    setCSSVariables(LayoutSetup.cssVariables)
    setTimeout(() => {
      disableSplashScreen()
    }, 500)
  }

  const setToolbarType = (toolbarType: ToolbarType) => {
    const updatedConfig = {...config}
    if (updatedConfig.app?.toolbar) {
      updatedConfig.app.toolbar.layout = toolbarType
    }

    setLayoutIntoLocalStorage(updatedConfig)
    window.location.reload()
  }

  const setLayoutType = (layoutType: LayoutType) => {
    const updatedLayout = {...config, layoutType}
    setLayoutIntoLocalStorage(updatedLayout)
    window.location.reload()
  }

  const value: LayoutContextModel = {
    config,
    classes,
    attributes,
    cssVariables,
    setLayout,
    setLayoutType,
    setToolbarType,
    company,
    setCompany,
    financialYear,
    fyStartDate,
    setFyStartDate,
    fyEndDate,
    setFyEndDate,
    setFinancialYear,
    trigger,
    setTrigger,
    companyOptions,
    setCompanyOptions,
  }

  useEffect(() => {
    disableSplashScreen()
  }, [])

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
}

export {LayoutContext, LayoutProvider}

export function useLayout() {
  return useContext(LayoutContext)
}
